import { useEffect, useRef, useState } from 'react';

interface FlipNumberProps {
  value: number;
  suffix?: string;
  duration?: number;
  triggerOnView?: boolean;
  pad?: number;
  className?: string;
}

export default function FlipNumber({
  value,
  suffix = '',
  duration = 0,
  triggerOnView = false,
  pad = 1,
  className = '',
}: FlipNumberProps) {
  const [display, setDisplay] = useState(duration === 0 ? value : 0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (duration === 0) {
      setDisplay(value);
      return;
    }

    let raf = 0;
    let cancelled = false;

    const start = () => {
      const t0 = performance.now();
      const step = (now: number) => {
        if (cancelled) return;
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(eased * value));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };

    if (triggerOnView) {
      const el = ref.current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            start();
            obs.disconnect();
          }
        },
        { threshold: 0.5 }
      );
      obs.observe(el);
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf);
        obs.disconnect();
      };
    }

    start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [value, duration, triggerOnView]);

  return (
    <span ref={ref} className={`inline-block tabular-nums ${className}`}>
      {String(display).padStart(pad, '0')}
      {suffix}
    </span>
  );
}